import { useEffect, useRef } from "react";
import { toast } from "sonner";

/**
 * Live WebSocket hook. Subscribes to company events (trip/fueling/expense/maintenance/document/message/tracking).
 * onEvent(evt) is called for every event. When null, only toast notifications happen.
 */
export function useLiveEvents(onEvent) {
  const wsRef = useRef(null);
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const proto = backend.startsWith("https") ? "wss" : "ws";
    const host = backend.replace(/^https?:\/\//, "");
    const url = `${proto}://${host}/api/ws?token=${encodeURIComponent(token)}`;

    let closed = false;
    let retryTimer = null;

    const connect = () => {
      if (closed) return;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const evt = JSON.parse(e.data);
          cbRef.current?.(evt);
        } catch {}
      };
      ws.onclose = () => {
        if (!closed) retryTimer = setTimeout(connect, 3000);
      };
      ws.onerror = () => { try { ws.close(); } catch {} };
    };

    connect();
    return () => {
      closed = true;
      if (retryTimer) clearTimeout(retryTimer);
      try { wsRef.current?.close(); } catch {}
    };
  }, []);
}

export function labelForEntity(entity) {
  return ({
    trip: "Frete", fueling: "Abastecimento", expense: "Despesa",
    maintenance: "Manutenção", document: "Documento", message: "Mensagem",
    tracking: "Localização",
  })[entity] || entity;
}

export function actionLabel(action) {
  return ({ created: "criou", updated: "atualizou", deleted: "removeu", ping: "atualizou" })[action] || action;
}

export function toastEvent(evt) {
  if (!evt || !evt.actor) return;
  if (evt.entity === "tracking") return; // silent — map updates directly
  toast.info(`${evt.actor.name || "Alguém"} ${actionLabel(evt.action)} ${labelForEntity(evt.entity).toLowerCase()}`, { duration: 2500 });
}
